import React from "react";
import LazyImage from "web/react/components/lazy-load-image/lazy-load-image";
import { StoryDetails } from "web/react/emo/story-details";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useStoryAnalytics } from "web/react/hooks/use-story-analytics";
import { StoryCardSerializer } from "web/types/serializers";
import * as styles from "./story-card.css";

interface SecondaryStoryProps extends StoryCardSerializer {
    railPosition: number;
    position: number;
    lazy?: boolean;
    alt?: string;
}

export function StoryCard({
    id: componentId,
    title,
    caption,
    url,
    component_label: componentLabel,
    desktop_image_url: desktopImage,
    mobile_image_url: mobileImage,
    is_sponsored: isSponsored,
    position,
    railPosition,
    lazy = false,
    alt,
}: SecondaryStoryProps): React.ReactElement {
    const { isMobileViewport } = useDomViewport();

    const lazyImageSrc = isMobileViewport ? mobileImage : desktopImage;

    const { containerRef, sendClickEvent } = useStoryAnalytics({
        label: "secondary_story",
        componentId,
        componentLabel,
        position,
        isSponsored,
        railPosition,
    });

    return (
        <div ref={containerRef} className={styles.storyCard} data-testid="secondary-story">
            {lazy ? (
                <a href={url} onClick={() => sendClickEvent("image")} data-testid={"image"}>
                    <LazyImage src={lazyImageSrc} className={styles.image} alt={alt} />
                </a>
            ) : (
                <a href={url} onClick={() => sendClickEvent("image")} data-testid={"image"}>
                    <picture>
                        <source
                            data-testid="mobile-image-source"
                            media={styles.mobileMedia}
                            srcSet={mobileImage}
                        />
                        <source
                            data-testid="desktop-image-source"
                            media={styles.desktopMedia}
                            srcSet={desktopImage}
                        />
                        <img
                            data-test-id="image"
                            src={mobileImage}
                            className={styles.image}
                            alt={alt}
                        />
                    </picture>
                </a>
            )}
            <View width="full">
                <StoryDetails
                    isBodyClickable={isMobileViewport}
                    variant="compact"
                    details={{
                        isSponsored,
                        title,
                        caption,
                        url,
                    }}
                    onCtaClick={() => sendClickEvent("cta")}
                />
            </View>
        </div>
    );
}
